import { Component, OnChanges, Input, ViewChild, ElementRef, ChangeDetectorRef, Output, EventEmitter, SimpleChanges } from '@angular/core'
import { DisputeQuotaType, disputeQuotaTypeLabel } from '@utils/small-business-quota.util'
import { Observable, EMPTY, Subject, concat, of, distinctUntilChanged, switchMap, map, BehaviorSubject, firstValueFrom } from 'rxjs'
import { Product, ProductClass, ProductSearch } from '@models/product.model'
import { DocumentSubElement } from '@models/demand/demand-formalization-document.model'
import { tap, finalize, debounceTime } from 'rxjs/operators'
import { normalize } from '@utils/str.util'
import { normalizeMaskedAmount } from '@utils/masked-number.util'
import { ProductService } from '@services/product.service'
import { ExpenditureSubElementService } from '@services/expenditure-sub-element.service'
import { DemandIntentDocumentItem } from '@models/demand/demand-intent-document-item.model'
import { HttpParams } from '@angular/common/http'
import { Paginate, emptyPaginateObservable } from '@models/paginate.model'
import { DemandIntentDocumentItemService } from '@services/demand-intent-document-item.service'
import { DemandIntentDocument, DemandIntentDocumentPublishStatus } from '@models/demand/demand-intent-document.model'
import { FormGroup, FormControl, Validators, FormArray, AbstractControl } from '@angular/forms'
import { ToastService } from '@common/services'
import { NgbModal, NgbModalRef, NgbAccordionDirective } from '@ng-bootstrap/ng-bootstrap'
import { ImportService } from '@services/import.service'
import { LabelService } from '@services/label.service'
import { ScrollUtil } from '@utils/scroll.util'
import { AlertifyService } from '@services/alertify.service'
import { promisePool } from '@utils/pool.util'
import { ProcessBidType, isTechniqueOnlyCriteria } from '@models/planning/process.model'
import { NoticeSessionBatch, NoticeSessionBatchPartialRequest } from '@models/planning/notice-session-batch.model'
import { NoticeSessionBatchService } from '@services/notice-session-batch.service'

@Component({
	selector: 'app-items',
	templateUrl: './items.component.html',
	styleUrl: './items.component.scss',
	standalone: false
})
export class ItemsComponent implements OnChanges {
	@ViewChild('modalItems') modalAddItemsRef: NgbModalRef
	@ViewChild('accordionItems') accordionItemsRef?: NgbAccordionDirective
	@ViewChild('inputItemTotal') inputItemTotal?: ElementRef<HTMLInputElement>

	@Input() demand!: DemandIntentDocument
	@Output() formItemsChange = new EventEmitter<FormGroup<{ forms: FormArray }>>()

	modalAddItemRefOpen: boolean
	modalAddItemRef: NgbModalRef

	editable: boolean
	submitted: boolean
	listableItems: boolean
	itemInputExtras: boolean
	readSheetForImportItemsLoading: boolean

	totalItems: number
	itemsSubtotal: number
	submittedItems: Array<boolean> = []
	formItems = this.createFormItems()
	totalItemsLoading = new BehaviorSubject<boolean>(true)
	itemsSubtotalLoading = new BehaviorSubject<boolean>(false)

	productLoading: boolean
	product$: Observable<Array<Product>> = EMPTY
	productInput$ = new Subject<string>()

	productClassesLoading: boolean
	productClasses$: Observable<Array<ProductClass>> = EMPTY
	productClassesInput$ = new Subject<string>()

	productElementsLoading: boolean
	productElementsInput$ = new Subject<string>()
	productElements$: Observable<Array<DocumentSubElement>> = EMPTY

	demandIntentDocumentItem: DemandIntentDocumentItem
	demandIntentDocumentItemParams = new HttpParams()
	demandIntentDocumentItem$: Observable<Paginate<DemandIntentDocumentItem>> = emptyPaginateObservable()
	activeTabItemsModal: 'items' | 'batches' = 'items'
	noticeSessionBatchesLoading: boolean
	noticeSessionBatchSubmitting: boolean
	noticeSessionBatches: NoticeSessionBatch[] = []

	notificationItems: Array<{
		id: any
		message: string
		type: 'error' | 'success' | 'warn'
		unitValidate?: boolean
	}> = []

	protected readonly ScrollUtil = ScrollUtil
	protected readonly DemandIntentDocumentPublishStatus = DemandIntentDocumentPublishStatus
	protected readonly ProcessBidType = ProcessBidType

	constructor(
		private modal: NgbModal,
		public label: LabelService,
		private toast: ToastService,
		private alertfy: AlertifyService,
		private importService: ImportService,
		private productService: ProductService,
		private changeDetectorRef: ChangeDetectorRef,
		private expenditureSubElementService: ExpenditureSubElementService,
		public demandIntentDocumentItemService: DemandIntentDocumentItemService,
		private noticeSessionBatchService: NoticeSessionBatchService
	) {}

	/**
	 * O total segue a demanda, não a criação do componente: no cadastro ele nasce sem demanda e, ao salvar, a mesma
	 * instância passa a mostrar o processo criado — buscando só no `ngOnInit`, o "Total de" ficava vazio.
	 */
	ngOnChanges(changes: SimpleChanges) {
		const change = changes['demand']
		if (!change || change.previousValue?.id === change.currentValue?.id) return

		this.totalItems = undefined
		this.fetchTotalItems()
	}

	modalFormAddItems() {
		if (this.modalAddItemRefOpen) {
			return
		}

		this.fetchProducts()
		this.fetchProductClasses()
		this.fetchProductElements()

		if (!this.id) {
			this.editable = true

			if (this.formItems.controls.forms.length === 0) {
				this.addFormItem()
			}
		} else {
			this.listableItems = true
			this.activeTabItemsModal = 'items'
			this.fetchDemandIntentDocumentItemPageable()
			this.editable = this.demand.publishStatus !== DemandIntentDocumentPublishStatus.DONE

			if (this.isBatchBidType) {
				this.fetchNoticeSessionBatches()
			}
		}

		this.modalAddItemRef = this.modal.open(this.modalAddItemsRef, {
			size: 'xl',
			keyboard: false,
			backdrop: 'static'
		})

		this.modalAddItemRef.shown.subscribe(() => {
			this.modalAddItemRefOpen = true
		})
		this.modalAddItemRef.dismissed.subscribe(() => {
			this.modalAddItemRefOpen = false

			if (!this.id) {
				this.recreateItemFormControls()
			}
		})
		this.modalAddItemRef.closed.subscribe(() => {
			this.modalAddItemRefOpen = false
		})
	}

	// Bug do ngx-mask: reabrir o modal reaproveitando os MESMOS FormControl/FormGroup de uma sessão
	// de digitação anterior faz os campos com mask numérica (total/amount) exibirem "NaN" assim que o
	// <input> é recriado pelo modal — mesmo que o valor já esteja limpo (number). Só recriar o
	// FormGroup inteiro, com o modal já fechado (input destruído), evita o bug. Roda no callback
	// `dismissed` (depois que o DOM do modal já foi destruído), nunca enquanto o input ainda existe.
	private recreateItemFormControls() {
		const existingForms = [...this.itemsControl.controls]
		this.itemsControl.controls = []

		for (const form of existingForms) {
			const item = new DemandIntentDocumentItem({
				id: form.get('id')?.value,
				total: normalizeMaskedAmount(form.get('total')?.value),
				amount: normalizeMaskedAmount(form.get('amount')?.value),
				productUnit: form.get('unit')?.value,
				productDescription: form.get('specification')?.value,
				batchId: form.get('batchId')?.value,
				// `product`, não `selectProduct`: para produto NOVO (digitado), o persistProducts grava
				// o id criado no control `product` — o `selectProduct` segue com o objeto digitado, sem
				// id. Recriar a partir dele descartava o id e o salvamento caía no servidor com
				// "productId não deve ser nulo". No produto de catálogo os dois apontam para o mesmo
				// objeto e nada muda.
				product: form.get('product')?.value ?? form.get('selectProduct')?.value
			})

			// addFormItem() só popula productClasses/productSubElements a partir dos dados do
			// produto selecionado (item.product.classes/elements) — os valores que o usuário editou
			// manualmente nesses campos (seção "Incluir mais detalhes") vêm do form em si, não do
			// produto, e precisam ser preservados explicitamente aqui.
			const newForm = this.addFormItem(item)
			newForm.get('productClasses')?.setValue(form.get('productClasses')?.value)
			newForm.get('productSubElements')?.setValue(form.get('productSubElements')?.value)
		}
	}

	editFormItem(item: DemandIntentDocumentItem) {
		this.demandIntentDocumentItem = item
		this.listableItems = false

		item.product = new Product({
			id: item.productId,
			name: item.productName,
			unit: item.productUnit,
			description: item.productDescription
		})

		const form = this.addFormItem(item)

		form.disable()
		form.get('unit').enable()
		form.get('total').enable()
		form.get('amount').enable()
		form.get('specification').enable()
		form.get('batchId').enable()
	}

	delFormItem(index: number, item?: DemandIntentDocumentItem, pageable?: Paginate<DemandIntentDocumentItem>) {
		this.alertfy.confirm('Atenção', `Deseja realmente remover o item?`, event => {
			if (event.confirmed) {
				if (item) {
					item.deleting = true
					this.demandIntentDocumentItemService
						.remove(item.id)
						.pipe(finalize(() => (item.deleting = false)))
						.subscribe(() => {
							this.totalItems -= 1
							pageable.totalElements -= 1
							pageable.empty = pageable.totalElements === 0
							pageable.content.splice(index, 1)
							this.fetchItemsSubtotal()
							this.toast.success('Item removido com sucesso.')
						})
				} else {
					this.itemsControl.controls.splice(index, 1)

					if (this.notificationItems) {
						const notification = this.notificationItems.map(it => it.id).indexOf(index)

						if (notification > -1) {
							this.notificationItems.splice(notification, 1)

							if (notification + 1 <= this.notificationItems.length) {
								for (const item of this.notificationItems.slice(notification, this.notificationItems.length)) {
									item.id -= 1
								}

								this.changeDetectorRef.detectChanges()
							}
						}
					}
				}
			}
		})
	}

	/** Tipo de cota ME/EPP do item na disputa (spec 045): o do lote, na disputa por lote. Nulo = sem tipo. */
	quotaTypeLabel(item: DemandIntentDocumentItem): string | null {
		const type = item.batchQuotaType ?? item.quotaType
		return type ? disputeQuotaTypeLabel(type) : null
	}

	/**
	 * Ligação da cota reservada com a principal (spec 045): "cota reservada do item N" na reservada e "cota principal,
	 * reservada no item N" na origem. Procura a outra cota na página carregada; fora dela, o texto sai sem o número.
	 */
	quotaRelation(item: DemandIntentDocumentItem, page: DemandIntentDocumentItem[]): string | null {
		const type = item.batchQuotaType ?? item.quotaType

		if (type === DisputeQuotaType.RESERVED) {
			const source = page.find(it => it.id === item.quotaSourceItemId)
			return source ? `cota reservada do item ${source.ordering}` : item.quotaSourceItemId ? 'cota reservada de outro item' : null
		}

		const reserved = page.find(it => (it.batchQuotaType ?? it.quotaType) === DisputeQuotaType.RESERVED && it.quotaSourceItemId === item.id)
		return reserved ? `cota principal, reservada no item ${reserved.ordering}` : null
	}

	/** Melhor técnica ou conteúdo artístico (art. 35): objeto uno — por item, um único item; por lote, um único lote (espelho da regra do servidor). */
	get bestTechnicalSingleObject(): boolean {
		return isTechniqueOnlyCriteria(this.demand?.process?.judgingCriteria)
	}

	get bestTechnicalItemLimitReached(): boolean {
		return this.bestTechnicalSingleObject && !this.isBatchBidType && (this.totalItems ?? 0) + this.itemsControl.controls.length >= 1
	}

	addFormItem(item?: DemandIntentDocumentItem) {
		if (!item && this.bestTechnicalItemLimitReached) {
			this.toast.warn(
				'No julgamento por melhor técnica ou conteúdo artístico o processo deve ter um único item — o objeto é uno e a remuneração do edital é uma só.'
			)
			return null
		}

		this.listableItems = false

		const id = item?.id ?? null
		const total = item?.total ?? null
		const amount = item?.amount ?? null
		const unit = item?.productUnit ?? null
		const product = item?.product ?? null
		const specification = item?.productDescription ?? null
		const batchId = item?.batchId ?? null
		const productClasses: ProductClass[] | null = item?.product?.classes ?? null
		const productSubElements: DocumentSubElement[] | null = item?.product?.elements ?? null

		const form = new FormGroup({
			id: new FormControl(id),
			specification: new FormControl(specification, Validators.compose([Validators.required])),
			batchId: new FormControl(batchId),
			unit: new FormControl(unit, Validators.compose([Validators.required])),
			total: new FormControl(total, Validators.compose([Validators.required])),
			amount: new FormControl(amount, Validators.compose([Validators.required])),
			productClasses: new FormControl<ProductClass[] | null>(productClasses),
			productSubElements: new FormControl<DocumentSubElement[] | null>(productSubElements),
			product: new FormControl<Product | null>(product, Validators.compose([Validators.required])),
			selectProduct: new FormControl<Product | null>(product, Validators.compose([Validators.required]))
		})

		this.itemsControl.controls.push(form)

		if (item) {
			if (item.importProductInvalideUnit) {
				this.notificationItems.push({
					id: this.itemsControl.controls.length - 1,
					message: 'A unidade informada é invalida',
					unitValidate: true,
					type: 'error'
				})
			}
		}

		form.get('selectProduct')?.valueChanges.subscribe(value => {
			if (value && !value?.id) {
				const product = new Product({
					persistence: true,
					name: value?.label ?? value?.name,
					label: value?.label ?? value?.name
				})

				form.get('product')?.setValue(product)
				form.get('productSubElements').enable()
				form.get('productClasses').enable()
			} else if (value && value?.id) {
				form.get('product')?.setValue(value)
				form.get('unit')?.setValue(value?.unit)
				form.get('specification')?.setValue(value?.description)
				form.get('productSubElements')?.setValue(value?.elements?.map(it => new DocumentSubElement(it)))
				form.get('productClasses')?.setValue(value?.classes?.map(it => new ProductClass(it)))
				form.get('productSubElements').disable()
				form.get('productClasses').disable()
				this.inputItemTotal?.nativeElement.focus?.()
			} else {
				form.get('product')?.setValue(null)
				form.get('productClasses')?.setValue(null)
				form.get('productSubElements')?.setValue(null)
				form.get('productSubElements').enable()
				form.get('productClasses').enable()
			}
		})

		this.formItemsChange.emit(this.formItems)

		return form
	}

	updateItem() {
		const form = this.itemsControl.controls[0]

		if (form.invalid) {
			form.markAllAsTouched()
			return
		}
		const unit = form.get('unit').value
		const total = normalizeMaskedAmount(form.get('total').value)
		const amount = normalizeMaskedAmount(form.get('amount').value)
		const specification = form.get('specification').value
		const batchId = this.getBatchIdFromForm(form)

		const values: any = {}

		if (total && total !== this.demandIntentDocumentItem.total) {
			values.total = total
		}

		if (amount && amount !== this.demandIntentDocumentItem.amount) {
			values.amount = amount
		}

		if (unit && unit.trim() !== this.demandIntentDocumentItem.productUnit) {
			values.productUnit = unit
		}

		if (specification && specification.trim() !== this.demandIntentDocumentItem.productDescription) {
			values.productDescription = specification
		}

		if (batchId !== (this.demandIntentDocumentItem.batchId ?? null)) {
			values.batchId = batchId
		}

		this.submitted = true

		this.demandIntentDocumentItemService
			.update(this.demandIntentDocumentItem.id, new DemandIntentDocumentItem(values))
			.pipe(finalize(() => (this.submitted = false)))
			.subscribe(() => {
				this.cancelFormItem()
				// A lista volta a buscar a página sozinha (a view é recriada e o async pipe reassina),
				// mas o subtotal é um número solto: sem este fetch ele continua exibindo o valor de
				// antes da edição.
				this.fetchItemsSubtotal()
				this.toast.success('Item atualizado com sucesso.')
			})
	}

	cancelFormItem() {
		this.listableItems = true
		this.itemsControl.controls = []
		this.demandIntentDocumentItem = null
	}

	hasItemSubmitted(index: number) {
		return this.submittedItems && this.submittedItems[index]
	}

	hasNotification(id: any) {
		return this.notificationItems.find(it => it.id === id)
	}

	getFormGroup(form: AbstractControl): FormGroup {
		return form as FormGroup
	}

	getFormControl(form: any, name: string): FormControl {
		return form.get(name) as FormControl
	}

	getFormControlInvalid(form: any, name: string): boolean {
		const input = this.getFormControl(form, name)
		return input?.touched && input?.invalid
	}

	async modalFromAddVerifyItems() {
		this.formItems.markAllAsTouched()
		this.accordionItemsRef.collapseAll()

		for (const [i, formItem] of this.itemsControl.controls.entries()) {
			if (formItem.invalid) {
				ScrollUtil.scrollIntoViewToClassName(`accordion-item-${i}`)
				this.toast.warn(`Verifique os campos do item ${i + 1} para continuar.`)
				return
			} else if (this.shouldValidateBatchForItem && !this.getBatchIdFromForm(formItem)) {
				ScrollUtil.scrollIntoViewToClassName(`accordion-item-${i}`)
				this.toast.warn(`Informe o lote do item ${i + 1} para continuar.`)
				return
			} else {
				const notification = this.notificationItems.find(it => it.id === i)

				if (notification && notification.unitValidate) {
					const index = this.notificationItems.map(it => it.id).indexOf(i)
					this.notificationItems.splice(index, 1)
				}
			}
		}

		this.submitted = true
		this.submittedItems = []
		this.notificationItems = []

		// Spec 020/RN-21: uma requisição em lote no lugar de uma por item.
		this.submittedItems = Array.from({ length: this.itemsControl.controls.length }, () => true)

		const results = await this.productService.persistProducts(this.itemsControl.controls)

		for (const result of results) {
			if (result.status === 'fulfilled') {
				const value = result.value

				this.submittedItems[value.index] = false

				if (!value.success) {
					this.notificationItems.push({
						id: value.index,
						message: 'Tívemos um problema ao salvar o item, altere o nome e ou descrição, ou tente novamente.',
						type: 'error'
					})
				}
			}
		}

		this.submittedItems = []

		if (this.notificationItems.some(it => it.type === 'error')) {
			this.submitted = false
			return
		}

		if (this.id) {
			await firstValueFrom(this.demandIntentDocumentItemService.save(this.id, this.mapperEntityItem()).pipe(finalize(() => (this.submitted = false))))

			this.cancelFormItem()
			this.fetchTotalItems()
			this.fetchItemsSubtotal()
		} else {
			this.submitted = false
			this.modalAddItemRef?.dismiss()
		}
	}

	async readSheetForImportItems(event: any) {
		if (!(event.target && event.target.files.length > 0)) {
			return
		}

		const file = event.target.files[0]
		event.target.value = ''

		if (!this.id && this.itemsControl.controls.length > 0) {
			this.alertfy.confirm('Atenção', 'Já existem itens preenchidos no formulário. Deseja limpar a lista atual antes de importar a planilha?', result => {
				if (result.confirmed) {
					this.importSheetItems(file)
				}
			})
			return
		}

		await this.importSheetItems(file)
	}

	private async importSheetItems(file: File) {
		this.formItems = this.createFormItems()
		this.notificationItems = []
		this.readSheetForImportItemsLoading = true

		const dataSheet = await this.importService.readSheetWorkbookDemandItems(file)

		if (dataSheet.error) {
			this.toast.error('Tívemos um problema ao processar sua importação!')
			this.readSheetForImportItemsLoading = false
			this.changeDetectorRef.detectChanges()
			return
		}

		const dataSheetItems = dataSheet.items

		if (dataSheetItems.length === 0) {
			this.toast.info('Nenhum registro foi importado!')
			this.readSheetForImportItemsLoading = false
			return
		}

		const units = this.label.productUnits.map(it => it.value)
		const elements = await firstValueFrom(this.expenditureSubElementService.subElements())
		const classCache = new Map<string, ProductClass | null>()
		const productCache = new Map<string, Product | null>()
		const importedItems = new Array<DemandIntentDocumentItem>(dataSheetItems.length)
		const tasks = dataSheetItems.map((data, index) => {
			return async () => {
				const productClasses = []
				const productElements = []
				const unit = units.find(it => it === data.unit?.trim()?.toUpperCase())

				let itemProduct = new Product({
					unit,
					name: data.product,
					classes: productClasses,
					elements: productElements,
					description: data.specification
				})

				if (data.productClass) {
					const code = data.productClass.split('-').shift().trim()

					if (code) {
						if (!classCache.has(code)) {
							classCache.set(code, await firstValueFrom(this.productService.classExternalCode(code)))
						}

						const productClass = classCache.get(code)

						if (productClass && productClass.id) {
							productClasses.push(productClass)
						}
					}
				}

				if (data.productElement) {
					const code = data.productElement.split('-').shift().trim()

					if (code) {
						const productElement = elements.find(it => it.externalCode === code)

						if (productElement && productElement.id) {
							productElements.push(productElement)
						}
					}
				}

				const productName = normalize(data.product?.trim() ?? '')

				if (!productCache.has(productName)) {
					productCache.set(productName, await firstValueFrom(this.productService.detailByName(data.product)))
				}

				const product = productCache.get(productName)

				if (product && product.id) {
					itemProduct = product

					if (itemProduct.classes && itemProduct.classes.length > 0) {
						if (!itemProduct.classes.some(it => productClasses.map(pc => pc.id).includes(it.id))) {
							itemProduct.classes.push(...productClasses)
						}
					} else if (productClasses.length > 0) {
						itemProduct.classes = productClasses
					}

					if (itemProduct.elements && itemProduct.elements.length > 0) {
						if (!itemProduct.elements.some(it => productElements.map(pc => pc.id).includes(it.id))) {
							itemProduct.elements.push(...productElements)
						}
					} else if (productElements.length > 0) {
						itemProduct.elements = productElements
					}
				} else {
					itemProduct.persistence = true
				}

				const item = DemandIntentDocumentItem.from(itemProduct, data.total)
				item.amount = data.unitValue

				if (!item.product.unit) {
					item.importProductInvalideUnit = true
				}

				importedItems[index] = item
			}
		})

		await promisePool(tasks, 10)

		const chunkSize = 50

		for (let index = 0; index < importedItems.length; index += chunkSize) {
			const chunk = importedItems.slice(index, index + chunkSize).filter(Boolean)

			for (const item of chunk) {
				this.addFormItem(item)
			}

			await new Promise(resolve => setTimeout(resolve, 0))
		}

		this.toast.success(dataSheetItems.length > 1 ? `Foram importados ${dataSheetItems.length} itens.` : `Foi importado 1 item.`)
		this.readSheetForImportItemsLoading = false
		this.changeDetectorRef.detectChanges()

		setTimeout(() => {
			this.accordionItemsRef?.collapseAll()
			ScrollUtil.scrollIntoViewToClassName(`accordion-item-${dataSheetItems.length - 1}`)
		}, 200)
	}

	async downloadSheetModel() {
		const response = await fetch('./assets/demand_imported_items.xlsx')
		const file = await response.blob()
		const link = document.createElement('a')
		link.href = URL.createObjectURL(file)
		link.download = `IMPORTACAO_ITENS_SILGOV_${new Date().toLocaleDateString()}.xlsx`
		link.click()
	}

	private mapperEntityItem() {
		return this.itemsControl.controls.map(form => {
			const product = form.get('product')?.value as Product
			const total = normalizeMaskedAmount(form.get('total')?.value)
			const amount = normalizeMaskedAmount(form.get('amount')?.value)
			const specification = form.get('specification')?.value
			const batchId = this.getBatchIdFromForm(form)
			const item = DemandIntentDocumentItem.from(product, total)

			delete item.product

			item.productDescription = specification
			item.unitId = this.demand.unit.id
			item.amount = amount
			item.batchId = batchId

			return item
		})
	}

	updateItemBatch(item: DemandIntentDocumentItem, batchId: string | null) {
		if ((item.batchId ?? null) === (batchId ?? null)) {
			return
		}

		item.submitted = true

		this.demandIntentDocumentItemService
			.update(item.id, new DemandIntentDocumentItem({ batchId }))
			.pipe(finalize(() => (item.submitted = false)))
			.subscribe(() => {
				this.applyBatchToItem(item, batchId)
				this.toast.success('Lote do item atualizado com sucesso.')
			})
	}

	createNoticeSessionBatch() {
		if (!this.noticeSessionId) {
			this.toast.warn('Sessão do edital não informada para cadastrar lotes.')
			return
		}

		if (this.bestTechnicalSingleObject && this.noticeSessionBatches.length >= 1) {
			this.toast.warn(
				'No julgamento por melhor técnica ou conteúdo artístico o processo deve ter um único lote — o objeto é uno e a remuneração do edital é uma só.'
			)
			return
		}

		this.alertfy.prompt('Cadastrar lote', 'Descrição', event => {
			if (event.confirmed) {
				const description = event.value?.trim()

				if (!description) {
					this.toast.warn('Informe a descrição do lote.')
					return
				}

				const entity = new NoticeSessionBatch({
					description,
					ordering: this.noticeSessionBatches.length + 1,
					noticeSession: { id: this.noticeSessionId } as any
				})

				this.noticeSessionBatchSubmitting = true
				this.noticeSessionBatchService
					.save(entity)
					.pipe(finalize(() => (this.noticeSessionBatchSubmitting = false)))
					.subscribe(() => {
						this.fetchNoticeSessionBatches()
						this.toast.success('Lote cadastrado com sucesso.')
					})
			}
		})
	}

	editNoticeSessionBatch(batch: NoticeSessionBatch) {
		this.alertfy.prompt('Editar lote', 'Descrição', event => {
			if (event.confirmed) {
				const description = event.value?.trim()

				if (!description || description === batch.description) {
					this.toast.warn('Informe uma nova descrição para o lote.')
					return
				}

				this.noticeSessionBatchService.update(batch.id, new NoticeSessionBatchPartialRequest({ description })).subscribe(() => {
					batch.description = description
					batch.label = description
					this.toast.success('Lote atualizado com sucesso.')
				})
			}
		})
	}

	removeNoticeSessionBatch(batch: NoticeSessionBatch) {
		this.alertfy.confirm('Atenção', 'Deseja realmente remover este lote?', event => {
			if (event.confirmed) {
				this.noticeSessionBatchService.remove(batch.id).subscribe(() => {
					this.noticeSessionBatches = this.noticeSessionBatches.filter(it => it.id !== batch.id)
					this.toast.success('Lote removido com sucesso.')
					this.fetchDemandIntentDocumentItemPageable()
				})
			}
		})
	}

	private createFormItems() {
		const form = new FormGroup({ forms: new FormArray([]) })
		this.formItemsChange.emit(form)
		return form
	}

	protected fetchTotalItems() {
		if (!this.id) return

		this.totalItemsLoading.next(true)
		this.demandIntentDocumentItemService
			.paginate(this.id)
			.pipe(finalize(() => this.totalItemsLoading.next(false)))
			.subscribe(response => {
				this.totalItems = response.totalElements
			})
	}

	protected fetchProducts() {
		this.product$ = concat(
			of([]),
			this.productInput$.pipe(
				distinctUntilChanged(),
				switchMap(term => {
					this.productLoading = true

					return this.productService.search(new ProductSearch({ term })).pipe(
						tap({ complete: () => (this.productLoading = false) }),
						map(response => response.content ?? [])
					)
				})
			)
		)
	}

	protected fetchProductClasses() {
		this.productClasses$ = concat(
			of([]),
			this.productClassesInput$.pipe(
				distinctUntilChanged(),
				switchMap(term => {
					this.productClassesLoading = true

					return this.productService.classesSearch(new ProductSearch({ term })).pipe(
						tap({ complete: () => (this.productClassesLoading = false) }),
						map(response => (response.content ?? []).map(it => new ProductClass(it)))
					)
				})
			)
		)
	}

	protected fetchProductElements() {
		const source = (term?: string) => {
			return this.expenditureSubElementService.subElements().pipe(
				tap(() => (this.productElementsLoading = true)),
				finalize(() => (this.productElementsLoading = false)),
				map(elements => {
					const filtered = !term || term.length < 2 ? elements.slice(0, 50) : elements.filter(it => it.term?.includes(normalize(term))).slice(0, 10)

					return Array.from(new Map(filtered.map(it => [normalize(it.name ?? ''), it])).values())
				})
			)
		}

		this.productElements$ = concat(
			source(),
			this.productElementsInput$.pipe(
				debounceTime(200),
				distinctUntilChanged(),
				switchMap(term => source(term))
			)
		)
	}

	protected fetchDemandIntentDocumentItemPageable(page = 0, params?: HttpParams) {
		this.demandIntentDocumentItemParams = params ?? this.demandIntentDocumentItemParams

		if (!this.demandIntentDocumentItemParams) {
			this.demandIntentDocumentItemService.size = 10
			this.demandIntentDocumentItemParams = new HttpParams().set('page', page)
		} else {
			this.demandIntentDocumentItemParams = this.demandIntentDocumentItemParams.set('page', page)
		}

		this.demandIntentDocumentItem$ = this.demandIntentDocumentItemService.paginate(
			this.demand.id,
			this.demandIntentDocumentItemParams,
			this.demandIntentDocumentItemService.searchTerm
		)

		this.fetchItemsSubtotal()
	}

	protected fetchNoticeSessionBatches() {
		if (!this.noticeSessionId) {
			this.noticeSessionBatches = []
			return
		}

		this.noticeSessionBatchesLoading = true

		this.noticeSessionBatchService
			.all(this.noticeSessionId)
			.pipe(finalize(() => (this.noticeSessionBatchesLoading = false)))
			.subscribe(response => {
				this.noticeSessionBatches = (response ?? []).sort((a, b) => {
					if (a.ordering !== b.ordering) {
						return (a.ordering ?? 0) - (b.ordering ?? 0)
					}

					return (a.description ?? '').localeCompare(b.description ?? '')
				})
			})
	}

	private fetchItemsSubtotal() {
		if (!this.id) return

		this.itemsSubtotalLoading.next(true)
		this.demandIntentDocumentItemService
			.totals(this.demand.id, this.demandIntentDocumentItemParams, this.demandIntentDocumentItemService.searchTerm)
			.pipe(finalize(() => this.itemsSubtotalLoading.next(false)))
			.subscribe(response => {
				this.itemsSubtotal = response.total
			})
	}

	get itemsControl() {
		return this.formItems.get('forms') as FormArray
	}

	get isBatchBidType(): boolean {
		return this.demand?.process?.bidType === ProcessBidType.BATCH
	}

	get noticeSessionId(): string | null {
		return this.demand?.noticeSession?.id ?? null
	}

	get shouldValidateBatchForItem(): boolean {
		return this.isBatchBidType && this.noticeSessionBatches.length > 0
	}

	get id(): string {
		return this.demand?.id
	}

	private getBatchIdFromForm(form: any): string | null {
		return form?.get('batchId')?.value ?? null
	}

	private applyBatchToItem(item: DemandIntentDocumentItem, batchId: string | null) {
		const batch = this.noticeSessionBatches.find(it => it.id === batchId)
		item.batchId = batchId
		item.batchDescription = batch?.description ?? null
		item.batchExternalCode = batch?.externalCode ?? null
	}
}
